/**
 * API Health Context
 * Tracks consecutive API failures and triggers maintenance mode when server appears down
 *
 * Conservative thresholds to avoid false positives:
 * - Only counts 5xx errors and network failures (not 4xx auth errors)
 * - Requires 3+ consecutive failures within 30 seconds
 * - Automatically resets after successful request
 * - Does NOT block the app - just shows a dismissible warning
 */

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

interface ApiHealthContextType {
  isServerDown: boolean;
  reportError: (error: any) => void;
  reportSuccess: () => void;
  resetHealth: () => void;
  dismissMaintenance: () => void;
}

const ApiHealthContext = createContext<ApiHealthContextType | undefined>(undefined);

// Configuration - conservative to avoid false positives
const FAILURE_THRESHOLD = 3; // Number of consecutive failures before showing maintenance
const FAILURE_WINDOW_MS = 30000; // 30 seconds - failures must happen within this window
const COOLDOWN_MS = 60000; // 1 minute cooldown after user dismisses

export function ApiHealthProvider({ children }: { children: React.ReactNode }) {
  const [isServerDown, setIsServerDown] = useState(false);
  const failureTimestamps = useRef<number[]>([]);
  const dismissedUntil = useRef<number>(0);

  /**
   * Report an API error
   * Only counts server errors (5xx) and network failures
   * Ignores auth errors (401/403) as those are expected behavior
   */
  const reportError = useCallback((error: any) => {
    // Don't track if user dismissed the maintenance screen recently
    if (Date.now() < dismissedUntil.current) {
      return;
    }

    // Only count server errors (5xx) and network failures
    // Skip 4xx errors (auth issues, not found, etc.) as those are expected
    const statusCode = error?.statusCode || error?.status;
    const isServerError = statusCode >= 500 || statusCode === undefined; // undefined = network error
    const isNetworkError = error?.message?.includes('fetch') ||
                          error?.message?.includes('network') ||
                          error?.message?.includes('timeout') ||
                          error?.code === 'TIMEOUT';

    if (!isServerError && !isNetworkError) {
      return; // Don't count 4xx errors
    }

    const now = Date.now();

    // Add current failure timestamp
    failureTimestamps.current.push(now);

    // Remove old failures outside the window
    failureTimestamps.current = failureTimestamps.current.filter(
      ts => now - ts < FAILURE_WINDOW_MS
    );

    console.log(`[ApiHealth] Error tracked. Recent failures: ${failureTimestamps.current.length}/${FAILURE_THRESHOLD}`);

    // Check if we've hit the threshold
    if (failureTimestamps.current.length >= FAILURE_THRESHOLD) {
      console.warn('[ApiHealth] Multiple consecutive failures detected - showing maintenance screen');
      setIsServerDown(true);
    }
  }, []);

  /**
   * Report a successful API call
   * Resets the failure counter
   */
  const reportSuccess = useCallback(() => {
    if (failureTimestamps.current.length > 0) {
      console.log('[ApiHealth] Successful request - resetting failure counter');
      failureTimestamps.current = [];
    }

    // If server was marked as down, mark it as back up
    if (isServerDown) {
      console.log('[ApiHealth] Server appears to be back up');
      setIsServerDown(false);
    }
  }, [isServerDown]);

  /**
   * Manually reset health status
   */
  const resetHealth = useCallback(() => {
    failureTimestamps.current = [];
    setIsServerDown(false);
  }, []);

  /**
   * User dismisses the maintenance screen
   * Sets a cooldown before showing it again
   */
  const dismissMaintenance = useCallback(() => {
    console.log('[ApiHealth] User dismissed maintenance screen');
    dismissedUntil.current = Date.now() + COOLDOWN_MS;
    failureTimestamps.current = [];
    setIsServerDown(false);
  }, []);

  return (
    <ApiHealthContext.Provider
      value={{
        isServerDown,
        reportError,
        reportSuccess,
        resetHealth,
        dismissMaintenance,
      }}
    >
      {children}
    </ApiHealthContext.Provider>
  );
}

export function useApiHealth() {
  const context = useContext(ApiHealthContext);
  if (context === undefined) {
    throw new Error('useApiHealth must be used within an ApiHealthProvider');
  }
  return context;
}

/**
 * Optional hook version that doesn't throw if used outside provider
 * Useful for the API client which may run before provider is mounted
 */
export function useApiHealthOptional(): ApiHealthContextType | null {
  return useContext(ApiHealthContext) ?? null;
}

// Global event emitter for API client to report errors without React context
// This allows the API client to report errors even outside React components
type ApiHealthListener = {
  onError: (error: any) => void;
  onSuccess: () => void;
};

let globalListener: ApiHealthListener | null = null;

export function setApiHealthListener(listener: ApiHealthListener | null) {
  globalListener = listener;
}

export function reportApiError(error: any) {
  globalListener?.onError(error);
}

export function reportApiSuccess() {
  globalListener?.onSuccess();
}
